/**
 * Render templates and HTML fix-ups for the public political preview page,
 * which shows how an image creative looks with the TTPA transparency overlay
 * the ad server adds to political ads.
 */

export type PoliticalPreviewLayout = 'standard' | 'fullScreen'

const STANDARD_TEMPLATE = `<a id="{{adId}}" rel="nofollow" target="_blank" href="{{{urls.destination.url}}}">
<img src="{{{assets.Image.cdnId}}}" width="{{creativeWidth}}" height="{{creativeHeight}}" alt=""/>
</a>
{{{include.attention}}}

{{{politicalOverlay}}}

<style>
    .adn-ttpa-container,
    .adn-ttpa-container * {
        line-height: 1.2em !important;
    }
</style>`

const FULL_SCREEN_TEMPLATE = `<script>
  window.aim = window.aim || {};
  window.aim.format="fullScreen";
  (function() {
    try {
      var dummyFrame = document.createElement('iframe');
      Object.defineProperty(window, 'frameElement', {configurable: true, get: function() { return dummyFrame; }});
    } catch (e) {}
  })();
</script>
<script src="https://assets.adnuntius.com/aim.js"></script>
<style>
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: 100% !important;
    overflow: hidden !important;
  }
  .responseCtr,
  .contentWrapperPerItem {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    overflow: hidden !important;
  }
  .backgroundImage {
    width: 100% !important;
    height: 100% !important;
    display: block !important;
    overflow: hidden;
    background-position: 50% 50%;
    background-size: cover;
    background-repeat: no-repeat;
    background-image: url({{{assets.Image.cdnId}}});
  }
  .backgroundImage img {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    object-fit: cover !important;
    display: block !important;
    border: 0;
  }
  .adn-ttpa-container {
    top: 1px;
    bottom: auto !important;
  }
  .adn-ttpa-container,
  .adn-ttpa-container * {
    line-height: 1.2em !important;
  }
</style>
<a href="{{{urls.destination.url}}}" class="backgroundImage" rel="nofollow" target="_blank" style="background-image: url({{{assets.image.cdnId}}})">
  <img src="{{{assets.Image.cdnId}}}" alt="">
</a>

<script>
  (function() {
    var setFill = function(el) {
      if (!el || !el.style) {
        return;
      }
      el.style.setProperty('display', 'block', 'important');
      el.style.setProperty('width', '100%', 'important');
      el.style.setProperty('height', '100%', 'important');
      el.style.setProperty('max-width', 'none', 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('margin', '0', 'important');
      el.style.setProperty('padding', '0', 'important');
    };
    var fill = function() {
      setFill(document.documentElement);
      setFill(document.body);
      var nodes = document.querySelectorAll('.responseCtr, .contentWrapperPerItem, .backgroundImage');
      for (var i = 0; i < nodes.length; i++) {
        setFill(nodes[i]);
      }
      var imgs = document.querySelectorAll('.backgroundImage img');
      for (var j = 0; j < imgs.length; j++) {
        imgs[j].removeAttribute('width');
        imgs[j].removeAttribute('height');
        imgs[j].style.setProperty('object-fit', 'cover', 'important');
      }
    };
    fill();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fill);
    }
    window.addEventListener('load', fill);
  })();
</script>

{{{politicalOverlay}}}`

export function politicalPreviewTemplate(layout: PoliticalPreviewLayout): string {
  return layout === 'fullScreen' ? FULL_SCREEN_TEMPLATE : STANDARD_TEMPLATE
}

/** Message type the measuring script posts to the page hosting the preview. */
export const OVERLAY_MESSAGE_TYPE = 'adn-political-preview-overlay'

export interface OverlayMessage {
  type: typeof OVERLAY_MESSAGE_TYPE
  width?: number
  height?: number
  frameWidth?: number
  frameHeight?: number
  unavailable?: boolean
}

/**
 * Measures the transparency overlay from inside the frame and posts its size
 * to the parent. The preview runs sandboxed on an opaque origin, so the page
 * cannot reach into the frame to measure it itself. The overlay is added by
 * aim.js after load, so it is polled for a few seconds before giving up.
 */
const MEASURE_SCRIPT = `<script>
(function() {
  var tries = 0;
  function post(data) {
    data.type = '${OVERLAY_MESSAGE_TYPE}';
    parent.postMessage(data, '*');
  }
  function measure() {
    var el = document.querySelector('.adn-ttpa-container, [data-testid="adn-ttpa-container"], #adn-ttpa-container');
    if (el) {
      var rect = el.getBoundingClientRect();
      if (rect.width && rect.height) {
        post({
          width: Math.round(rect.width),
          height: Math.round(rect.height),
          frameWidth: window.innerWidth,
          frameHeight: window.innerHeight
        });
        return;
      }
    }
    if (++tries > 40) {
      post({unavailable: true});
      return;
    }
    setTimeout(measure, 75);
  }
  if (document.readyState === 'complete') {
    measure();
  } else {
    window.addEventListener('load', measure);
  }
})();
</script>`

/**
 * Readies the ad server's rendered preview for the frame: sets the document
 * language the overlay is written in, marks transparency links as previews,
 * fills in the destination and image, and adds the overlay measuring script.
 */
export function preparePoliticalPreviewHtml(
  html: string,
  destinationUrl: string,
  imageUrl: string,
  languageCode: string
): string {
  let result = html || ''

  if (languageCode) {
    if (/<html\b[^>]*\slang=/i.test(result)) {
      result = result.replace(/(<html\b[^>]*\slang=")([^"]*)(")/i, `$1${languageCode}$3`)
    } else if (/<html\b/i.test(result)) {
      result = result.replace(/<html\b/i, `<html lang="${languageCode}"`)
    }
  }

  // Links to the transparency page open it in preview mode, as no real ad exists
  result = result.replace(/(<a\b[^>]*\shref=")([^"]*)(")/gi, (_match, prefix, href, suffix) => {
    if (!href || !/\/(?:ad-)?transparency-preview(?:[/?#]|$)/.test(href)) {
      return prefix + href + suffix
    }
    const missing = []
    if (!href.includes('political=')) missing.push('political=true')
    if (!href.includes('preview=')) missing.push('preview=true')
    if (!missing.length) return prefix + href + suffix
    return prefix + href + (href.includes('?') ? '&' : '?') + missing.join('&') + suffix
  })

  if (destinationUrl) {
    result = result.replace(/\{\{\{urls\.destination\.url\}\}\}/g, destinationUrl)
    if (/<a\b[^>]*href=/i.test(result)) {
      result = result.replace(/(<a\b[^>]*\shref=")([^"]*)(")/i, `$1${destinationUrl}$3`)
    } else if (/<a\b/i.test(result)) {
      result = result.replace(/<a\b/i, `<a href="${destinationUrl}"`)
    }
  }

  if (imageUrl) {
    result = result.replace(/\{\{\{assets\.(?:Image|image)\.cdnId\}\}\}/g, imageUrl)
    result = result.replace(
      /(background-image\s*:\s*url\()(['"]?)(?:\{\{\{[^)]*\}\}\}|)\2(\))/gi,
      `$1$2${imageUrl}$2$3`
    )
    result = result.replace(/(<img\b[^>]*\ssrc=")(?:\{\{\{[^"]*\}\}\}|)?(")/gi, `$1${imageUrl}$2`)
  }

  const bodyEnd = result.lastIndexOf('</body>')
  if (bodyEnd > -1) return result.slice(0, bodyEnd) + MEASURE_SCRIPT + result.slice(bodyEnd)
  return result + MEASURE_SCRIPT
}

/** The language the overlay is rendered in: the one picked, else the browser's. */
export function resolvePreviewLanguage(selected: string): string {
  if (selected && selected !== 'browser') return selected
  return navigator.languages?.[0] || navigator.language || 'en'
}

export const PREVIEW_LANGUAGES = ['browser', 'no', 'sv', 'da', 'fi', 'de'] as const
